import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map, List } from 'immutable'
import {
  isEpisode,
  isGenreInterview,
} from '../../services/content/index'
import { H1, HEADING_TYPES } from '../Heading/index'
import Link from '../Link/index'
import {
  handleClick,
  shouldRenderHost,
  showTeaser,
  isYogaFitnessMeditation,
} from '../Spotlight/utils'
import {
  SERIES_CLICK,
  TITLE_CLICK,
} from '../../services/event-tracking/index'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_COURSE,
  TYPE_CONTENT_VIDEO,
} from '../../services/content-type/index'
import { formatDuration, truncate } from '../../theme/web-app/index'
import LearnerLevelLabel from '../LearnerLevelLabel/index'

/**
 * The metadata label for the spotlight
 * @param {Object} props - The props
 * @param {String} props.label - The label
 * @returns {JSX.Element} The label component for the spotlight metadata
 */
function MetadataLabel (props) {
  const { label } = props

  if (!label || label === '') {
    return null
  }

  return (
    <div className="spotlight-metadata__label-container">
      <span className="spotlight-metadata__label">
        { label }
      </span>
    </div>
  )
}

/**
 * The title of the series
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Boolean} props.mobile - Whether or not the title needs mobile treatment
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} The formatted series title component
 */
function MetadataSeriesTitle (props) {
  const {
    data,
    upstreamContextOnClick,
    mobile = false,
  } = props
  const seriesTitle = data.get('seriesTitle')
  const seriesPath = data.get('seriesPath')
  const contentSubtype = data.get('contentSubtype')
  const eventType = SERIES_CLICK
  const baseClassName = 'spotlight-metadata__title-series'
  const classNames = [baseClassName]

  if (mobile) {
    classNames.push(`${baseClassName}--mobile`)
  } else {
    classNames.push(`${baseClassName}--desktop`)
  }

  if (isYogaFitnessMeditation(contentSubtype)) {
    classNames.push(`${baseClassName}--yoga-fitness-meditation`)
  }

  if (!isEpisode(props)) {
    return null
  }

  return (
    <div className={classNames.join(' ')}>
      <Link
        to={seriesPath}
        className="spotlight-metadata__title-series-link"
        onClick={(e) => handleClick(e, props, eventType, upstreamContextOnClick)}
      >
        {seriesTitle}
      </Link>
    </div>
  )
}

/**
 * The title of the content
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.isPublished - Whether or not the content is published
 * @param {Boolean} props.publishedAtDateInFuture - Whether or not the published at date
 *  is in the future
 * @param {Function} props.upstreamContextOnClick - The function that will set the upstream context
 * @returns {JSX.Element} The formatted title component
 */
function MetadataTitle (props) {
  const {
    data,
    contentType,
    isPublished,
    publishedAtDateInFuture,
    upstreamContextOnClick,
  } = props
  const isSeries = contentType === TYPE_CONTENT_SERIES
  const isVideo = contentType === TYPE_CONTENT_VIDEO
  const title = data.get('title')
  const primaryCourseReference = data.get('primaryCourseReference')
  const url = isVideo && primaryCourseReference && primaryCourseReference.size > 0
    ? data.getIn(['primaryCourseReference', 'contentTo', 0, 'path'])
    : data.get('url')
  const eventType = isSeries ? SERIES_CLICK : TITLE_CLICK

  return (
    <H1
      as={HEADING_TYPES.H4}
      inverted
      className="spotlight-metadata__title"
    >
      {
        isPublished && !publishedAtDateInFuture
          ? <Link
              to={url}
              className="spotlight-metadata__title-link"
              onClick={(e) => handleClick(e, props, eventType, upstreamContextOnClick)}
            >
              {title}
            </Link>
          : title
      }
    </H1>
  )
}

/**
 * The teaser for the content
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Boolean} props.readMoreExpanded - Whether or not the teaser is expanded
 * @param {Function} props.setReadMoreExpanded - Function for setting the teaser to expanded
 * @returns {JSX.Element} The formatted teaser component
 */
function MetadataTeaser (props) {
  const {
    data,
  } = props
  const contentSubtype = data.get('contentSubtype')
  const teaser = data.get('teaser')

  if (!teaser || !showTeaser(contentSubtype)) {
    return null
  }

  return (
    <div className="spotlight-metadata__teaser">
      <p className="spotlight-metadata__teaser-content">
        <span
          className="spotlight-metadata__teaser-text"
        >
          {truncate(teaser, 150)}
        </span>
      </p>
    </div>
  )
}

/**
 * The season and episode metadata for content
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.contentType - The type of content
 * @returns {JSX.Element} The formatted season:episode component
 */
function MetadataSeasonEpisode (props) {
  const { contentType, data } = props
  const isSeries = contentType === TYPE_CONTENT_SERIES
  const season = data.get('season')
  const episode = data.get('episode')

  if (isSeries || (!season && !episode)) {
    return null
  }

  return (
    <span className="spotlight-metadata__season-episode">
      { `S${season}:E${episode}` }
    </span>
  )
}

/**
 * The host metadata
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} The formatted host component
 */
function MetadataHost (props) {
  const { data } = props
  const contentSubtype = data.get('contentSubtype')
  const host = data.get('host')
  const renderHost = shouldRenderHost(host, contentSubtype)

  if (!renderHost) {
    return null
  }

  return (
    <span className="spotlight-metadata__host">{host}</span>
  )
}

/**
 * The season and episode count metadata for a series
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.contentType - The type of content
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} The formatted season and episode count component
 */
function MetadataSeriesSeasonEpisodes (props) {
  const { data, staticText, contentType } = props

  if (contentType !== TYPE_CONTENT_SERIES) {
    return null
  }

  const seasonCount = data.get('seasonCount')
  const episodeCount = data.get('episodeCount')
  const seasonCountText = seasonCount === 1 ? staticText.get('season') : staticText.get('seasons')

  return (
    <>
      <span className="spotlight-metadata__season-count">
        {`${seasonCount} ${seasonCountText}`}
      </span>
      <span className="spotlight-metadata__episode-count">
        {`${episodeCount} ${staticText.get('episodes')}`}
      </span>
    </>
  )
}

/**
 * The style for fitness, yoga, meditation
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} The formatted style component
 */
function MetadataStyle (props) {
  const { data } = props
  const yogaStyle = data.get('yogaStyle')
  const fitnessStyle = data.get('fitnessStyle')
  const meditationStyle = data.get('meditationStyle')
  const style = yogaStyle || fitnessStyle || meditationStyle

  if (!style) {
    return null
  }

  return (
    <span className="spotlight-metadata__style">
      {style}
    </span>
  )
}

/**
 * The level for yoga and fitness
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} The formatted level component
 */
function MetadataLevel (props) {
  const { data } = props
  const yogaLevel = data.get('yogaLevel')
  const fitnessLevel = data.get('fitnessLevel')
  const level = yogaLevel || fitnessLevel

  if (!level) {
    return null
  }

  return (
    <span className="spotlight-metadata__level">
      {level}
    </span>
  )
}

/**
 * The feature duration
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} The formatted feature doration component
 */
function MetadataDuration (props) {
  const { data } = props
  const duration = data.get('duration')

  if (!duration) {
    return null
  }

  return (
    <span className="spotlight-metadata__duration">
      {formatDuration(duration)}
    </span>
  )
}

/**
 * The guest/cast
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} The formatted guest component
 */
function MetadataGuest (props) {
  const { data, staticText } = props
  const tags = List.isList(data.get('references')) ? data.get('references', List()) : List()
  const showGuest = isGenreInterview({ tags })
  const guests = data.get('guest')

  if (!showGuest) {
    return null
  }

  return (
    <span className="spotlight-metadata__guests">
      <span className="spotlight-metadata__guest-label">
        {`${staticText.get('with')} `}
      </span>
      <span className="spotlight-metadata__guest-text">
        {guests}
      </span>
    </span>
  )
}

/**
 * The footer portion of the spotlight metadata
 * @param {Object} props - The props
 * @returns {JSX.Element} The formatted metadata footer component
 */
function MetadataFooter (props) {
  const { data } = props
  return (
    <div className="spotlight-metadata__footer">
      <MetadataSeriesTitle {...props} mobile />
      <LearnerLevelLabel
        learnerLevelLabel={data.get('learnerLevelLabel')}
        className="spotlight-metadata__learner-level"
      />
      <MetadataHost {...props} />
      <MetadataStyle {...props} />
      <MetadataLevel {...props} />
      <MetadataSeasonEpisode {...props} />
      <MetadataSeriesSeasonEpisodes {...props} />
      <MetadataDuration {...props} />
      <MetadataGuest {...props} />
    </div>
  )
}

function MetadataCourse (props) {
  const { data } = props
  const logoImageUrl = data.getIn(['logo_image', 'logo_xl'])
  const displayType = data.get('displayType')

  return (
    <div className="spotlight-metadata__course">
      {logoImageUrl && (
        <div className="spotlight-metadata__course-logo">
          <img src={logoImageUrl} alt={data.get('title') || ''} />
        </div>
      )}
      <div className={`spotlight-metadata__course-description spotlight-metadata__course-description--${displayType}`}>
        {data.get('teaser')}
      </div>
    </div>
  )
}

/**
 * The Spotlight metadata
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.label - The type of content
 * @returns {JSX.Element} The formatted spotlight metadata component
 */
function SpotlightMetadata (props) {
  const {
    data,
    label,
  } = props
  const noData = !data || !Map.isMap(data) || !data.size
  const today = new Date()
  const publishedAtDate = new Date(data.get('created'))
  const publishedAtDateInFuture = publishedAtDate > today
  const isPublished = data.get('published')

  if (noData) {
    return null
  }

  if (data.get('contentType') === TYPE_CONTENT_COURSE) {
    return <MetadataCourse {...props} />
  }

  return (
    <div className="spotlight-metadata">
      <MetadataLabel label={label} />
      <MetadataSeriesTitle {...props} />
      <MetadataTitle
        {...props}
        isPublished={isPublished}
        publishedAtDateInFuture={publishedAtDateInFuture}
      />
      <MetadataTeaser
        {...props}
      />
      <MetadataFooter {...props} />
    </div>
  )
}

SpotlightMetadata.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  label: PropTypes.string,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        staticText: state.staticText.getIn(['data', 'spotlightMetadata', 'data']),
      }
    },
  ),
)(SpotlightMetadata)
